import { Facebook, Instagram, MessageCircle, ShieldCheck, Mail, Phone, MapPin, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from '../../i18n/LanguageContext';

function Footer() {
  const currentYear = new Date().getFullYear();
  const { t, dir } = useLanguage();

  return (
    <footer className="bg-gray-50 border-t border-gray-100" dir={dir}>

      {/* ── Newsletter banner ── */}
      <div className="bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-start">
              <h3 className="text-white text-xl font-black mb-1">{t('footer.newsletterTitle')}</h3>
              <p className="text-emerald-100 text-sm">{t('footer.newsletterDesc')}</p>
            </div>
            <div className="flex w-full md:w-auto gap-2">
              <input
                type="email"
                placeholder={t('footer.newsletterPlaceholder')}
                className="flex-1 md:w-64 bg-white/20 border border-white/30 rounded-xl px-4 py-3 text-sm text-white placeholder-emerald-200 outline-none focus:border-white transition-colors"
              />
              <button className="bg-white hover:bg-emerald-50 text-emerald-600 px-5 py-3 rounded-xl font-bold text-sm transition-colors flex items-center gap-2 shrink-0">
                <Send size={14} />
                <span className="hidden sm:inline">{t('footer.newsletterBtn')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-2 space-y-5">
            <Link to="/">
              <img src="/logo.png" alt="BenMoussa" className="h-14 w-auto" />
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://facebook.com"
                aria-label="Facebook"
                className="w-10 h-10 bg-white border border-gray-200 hover:bg-[#1877F2] hover:border-[#1877F2] rounded-xl flex items-center justify-center transition-all group"
              >
                <Facebook size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://wa.me/21655615989"
                aria-label="WhatsApp"
                className="w-10 h-10 bg-white border border-gray-200 hover:bg-[#25D366] hover:border-[#25D366] rounded-xl flex items-center justify-center transition-all group"
              >
                <MessageCircle size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://instagram.com"
                aria-label="Instagram"
                className="w-10 h-10 bg-white border border-gray-200 hover:bg-[#E4405F] hover:border-[#E4405F] rounded-xl flex items-center justify-center transition-all group"
              >
                <Instagram size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-4">
            <h4 className="text-gray-900 font-black text-sm uppercase tracking-wider">
              {t('footer.categoriesTitle')}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: t('footer.oils'), href: "/products" },
                { label: t('footer.honey'), href: "/products" },
                { label: t('footer.supplements'), href: "/products" },
              ].map((item) => (
                <li key={item.href + item.label}>
                  <a href={item.href} className="text-gray-500 hover:text-emerald-600 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Help */}
          <div className="space-y-4">
            <h4 className="text-gray-900 font-black text-sm uppercase tracking-wider">
              {t('footer.helpTitle')}
            </h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: t('footer.faq'), href: "/faq" },
                { label: t('footer.shippingPolicy'), href: "/shipping" },
                { label: t('footer.contactUs'), href: "/contact" },
              ].map((item) => (
                <li key={item.href + item.label}>
                  <a href={item.href} className="text-gray-500 hover:text-emerald-600 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="text-gray-900 font-black text-sm uppercase tracking-wider">
              {t('footer.contactTitle')}
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 text-gray-500">
                <MapPin size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <span>{t('footer.address')}</span>
              </div>
              <a href={`tel:${t('nav.phone')}`} className="flex items-center gap-3 text-gray-500 hover:text-emerald-600 transition-colors">
                <Phone size={16} className="text-emerald-500 shrink-0" />
                <span dir="ltr">{t('nav.phone')}</span>
              </a>
              <a href={`mailto:${t('footer.email')}`} className="flex items-center gap-3 text-gray-500 hover:text-emerald-600 transition-colors">
                <Mail size={16} className="text-emerald-500 shrink-0" />
                <span>{t('footer.email')}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
     

          <p className="text-gray-400 text-xs text-center">
            © {currentYear} BenMoussa — {t('footer.allRights')}
          </p>

          <div className="flex gap-5 text-xs text-gray-400">
            <a href="/privacy" className="hover:text-emerald-600 transition-colors">{t('footer.privacy')}</a>
            <a href="/terms" className="hover:text-emerald-600 transition-colors">{t('footer.terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
